import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/api";
import { Save, ArrowLeft, Plus, Trash2, RotateCcw, Copy, Check, Eye, EyeOff, Key, AlertCircle, Zap, ChevronRight, Building2 } from "lucide-react";
import { copyToClipboard } from "@/utils/clipboard";
import { useAuth } from "@/contexts/AuthContext";

/* ── Tabs ─────────────────────────────────────────────────────────────────── */
const TABS = [
  { key: "config", label: "Configuration" },
  { key: "keys", label: "API Keys" },
  { key: "curl", label: "cURL / Docs" },
];

/* ── Key row ──────────────────────────────────────────────────────────────── */
function KeyRow({ k, pipelineId, onRefresh }) {
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [showNew, setShowNew] = useState(true);

  const copy = (val) => { copyToClipboard(val); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const rotate = async () => {
    if (!window.confirm("Rotate this key? The old key will stop working immediately.")) return;
    setRotating(true);
    try {
      const r = await api.post(`/pipelines/${pipelineId}/keys/${k.id}/rotate`);
      setNewKey(r.data.full_key);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Rotation failed");
    } finally { setRotating(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this key? This cannot be undone.")) return;
    await api.delete(`/pipelines/${pipelineId}/keys/${k.id}`);
    onRefresh();
  };

  return (
    <div className="p-4 bg-white border border-slate-200 rounded-lg space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">{k.name}</p>
          <p className="text-xs text-slate-400 font-mono">{k.key_prefix}••••••••</p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${k.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            {k.is_active ? "Active" : "Revoked"}
          </span>
          {k.is_active && (
            <>
              <button onClick={rotate} disabled={rotating} className="p-1.5 hover:bg-amber-50 rounded text-slate-400 hover:text-amber-600" title="Rotate key">
                <RotateCcw size={13} className={rotating ? "animate-spin" : ""} />
              </button>
              <button onClick={del} className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-500" title="Delete key">
                <Trash2 size={13} />
              </button>
            </>
          )}
        </div>
      </div>
      {k.last_used_at && <p className="text-xs text-slate-400">Last used: {k.last_used_at?.slice(0, 10)}</p>}
      {newKey && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md space-y-2">
          <p className="text-xs font-semibold text-amber-800">New key — save it now. You won't see it again.</p>
          <div className="flex items-center gap-2">
            <code className={`flex-1 text-xs font-mono bg-white border border-amber-200 rounded px-2 py-1.5 break-all ${showNew ? "" : "filter blur-sm"}`}>{newKey}</code>
            <button onClick={() => setShowNew(s => !s)} className="p-1.5 hover:bg-amber-100 rounded text-amber-600">{showNew ? <EyeOff size={13} /> : <Eye size={13} />}</button>
            <button onClick={() => copy(newKey)} className="p-1.5 hover:bg-amber-100 rounded text-amber-600">{copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────────────────── */
export default function PipelineBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === "new";
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const userCompanyId = user?.company_id || null;

  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "config");
  const [schemas, setSchemas] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [keys, setKeys] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({
    name: "",
    description: "",
    schema_ids: [],
    min_fields_matched: 1,
    webhook_id: "",
    is_active: true,
    target_company_id: userCompanyId || "",
    ocr_pages: "",
    ocr_table_format: "",
    ocr_extract_images: false,
    ocr_extract_header: false,
    ocr_extract_footer: false,
    ocr_return_raw_text: false,
    llm_override: "",
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [newKeyName, setNewKeyName] = useState("Default Key");
  const [createdKey, setCreatedKey] = useState(null);
  const [showCreatedKey, setShowCreatedKey] = useState(true);
  const [copiedCreated, setCopiedCreated] = useState(false);
  const [creatingKey, setCreatingKey] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get("/schemas").then(r => setSchemas(r.data)).catch(() => {});
    api.get("/webhooks").then(r => setWebhooks(r.data)).catch(() => {});
    // Load companies list for superadmin so they can pick a target company
    if (isSuperadmin) {
      api.get("/admin/companies").then(r => setCompanies(r.data)).catch(() => {});
    }
    if (!isNew) {
      setLoading(true);
      Promise.all([
        api.get(`/pipelines/${id}`),
        api.get(`/pipelines/${id}/keys`),
      ]).then(([pl, ks]) => {
        const p = pl.data;
        setForm({
          name: p.name || "",
          description: p.description || "",
          schema_ids: p.schema_ids || [],
          min_fields_matched: p.min_fields_matched ?? 1,
          webhook_id: p.webhook_id || "",
          is_active: p.is_active ?? true,
          ocr_pages: p.ocr_pages || "",
          ocr_table_format: p.ocr_table_format || "",
          ocr_extract_images: p.ocr_extract_images ?? false,
          ocr_extract_header: p.ocr_extract_header ?? false,
          ocr_extract_footer: p.ocr_extract_footer ?? false,
          ocr_return_raw_text: p.ocr_return_raw_text ?? false,
          llm_override: p.llm_override || "",
          target_company_id: p.company_id || "",
        });
        setKeys(ks.data);
      }).catch(() => navigate("/pipelines")).finally(() => setLoading(false));
    }
  }, [id, isNew, navigate]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    // Superadmins must pick a target company for company-scoped pipelines
    if (isSuperadmin && !userCompanyId && !form.target_company_id && form.scope !== "global") {
      setError("Please select a company for this pipeline.");
      setSaving(false);
      return;
    }
    try {
      const payload = {
        ...form,
        webhook_id: form.webhook_id || null,
        ocr_pages: form.ocr_pages.trim() || null,
        ocr_table_format: form.ocr_table_format || null,
        target_company_id: form.target_company_id || null,
        llm_override: form.llm_override || null,
      };
      if (isNew) {
        const r = await api.post("/pipelines", payload);
        navigate(`/pipelines/${r.data.id}?tab=keys`);
      } else {
        await api.put(`/pipelines/${id}`, payload);
        setActiveTab("keys");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save pipeline");
    } finally { setSaving(false); }
  };

  const toggleSchema = (sid) => {
    setForm(f => ({
      ...f,
      schema_ids: f.schema_ids.includes(sid)
        ? f.schema_ids.filter(s => s !== sid)
        : [...f.schema_ids, sid],
    }));
  };

  const createKey = async () => {
    setCreatingKey(true);
    setCreatedKey(null);
    try {
      const r = await api.post(`/pipelines/${id}/keys`, { name: newKeyName });
      setCreatedKey(r.data.full_key);
      setShowCreatedKey(true);
      setKeys(ks => [...ks, r.data]);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create key");
    } finally { setCreatingKey(false); }
  };

  const refreshKeys = () => {
    api.get(`/pipelines/${id}/keys`).then(r => setKeys(r.data)).catch(() => {});
  };

  const copyCurl = () => {
    const curl = buildCurl();
    copyToClipboard(curl);
  };

  const buildCurl = () => `curl -X POST "${BACKEND_URL}/api/pipelines/${id}/ingest" \\
  -H "Content-Type: application/json" \\
  -H "X-Pipeline-Key: <YOUR_PIPELINE_KEY>" \\
  -d '{
  "filename": "document.pdf",
  "mime_type": "application/pdf",
  "file_url": "https://example.com/document.pdf"
}'`;

  const buildResponse = () => `{
  "document_id": "doc_abc123",
  "pipeline_id": "${id || '<pipeline_id>'}",
  "pipeline_name": "${form.name || 'My Pipeline'}",
  "pages_processed": 3,${form.ocr_return_raw_text ? `
  "ocr_raw_text": "Extracted plain text from all processed pages...",` : ""}
  "matched_templates": [
    {
      "schema_id": "schema_001",
      "schema_name": "Invoice Schema",
      "matched": true,
      "match_reason": "Identifier found — 4 field(s) extracted",
      "llm_used": ${form.llm_override === "force_off" ? "false" : "true"},
      "extracted_fields": {
        "invoice_number": "INV-2025-0042",
        "total_amount": "8750.00",
        "vendor_name": "Acme Corp",
        "due_date": "2025-03-31"
      }
    }
  ],
  "other_templates": [
    {
      "schema_id": "schema_002",
      "schema_name": "Receipt Schema",
      "matched": false,
      "match_reason": "Start identifier not found",
      "extracted_fields": {}
    }
  ]
}`;

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/pipelines" className="p-2 hover:bg-slate-100 rounded-md text-slate-500">
          <ArrowLeft size={16} />
        </Link>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Link to="/pipelines" className="hover:text-slate-800">Pipelines</Link>
          <ChevronRight size={14} />
          <span className="text-slate-900 font-semibold">{isNew ? "New Pipeline" : form.name || "Edit Pipeline"}</span>
        </div>
      </div>

      {/* Tabs */}
      {!isNew && (
        <div className="flex border-b border-slate-200">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? "border-[#006080] text-[#006080]" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle size={14} className="flex-shrink-0" /> {error}
        </div>
      )}

      {/* ── Config tab ──────────────────────────────────────────────────── */}
      {(isNew || activeTab === "config") && (
        <form onSubmit={handleSave} className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">General</h3>

            {/* Company selector — superadmin only, locked when user has own company */}
            {isSuperadmin && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">
                  <span className="inline-flex items-center gap-1.5"><Building2 size={11} /> Company <span className="text-red-400">*</span></span>
                </label>
                {userCompanyId ? (
                  /* User has a company — show it locked */
                  <div className="flex items-center gap-2 h-9 px-3 border border-slate-200 rounded-md bg-slate-50 text-sm text-slate-600">
                    <Building2 size={13} className="text-slate-400" />
                    {companies.find(c => c.id === userCompanyId)?.name || userCompanyId}
                    <span className="ml-auto text-xs text-slate-400">locked to your account</span>
                  </div>
                ) : (
                  /* Superadmin without company — must pick one */
                  <select
                    data-testid="pipeline-company-selector"
                    value={form.target_company_id}
                    onChange={e => setForm(f => ({ ...f, target_company_id: e.target.value }))}
                    required={form.scope !== "global"}
                    className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
                  >
                    <option value="">— Select company —</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-slate-400 mt-1">The company this pipeline belongs to. Use scope "Global" if you want it available to all companies.</p>
              </div>
            )}

            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Pipeline Name <span className="text-red-400">*</span></label>
              <input
                data-testid="pipeline-name-input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                required
                placeholder="e.g., Invoice + Receipt Multi-Doc"
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
                placeholder="What documents does this pipeline handle?"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Min Fields Matched</label>
                <input
                  data-testid="min-fields-input"
                  type="number"
                  min={0}
                  value={form.min_fields_matched}
                  onChange={e => setForm(f => ({ ...f, min_fields_matched: parseInt(e.target.value) || 0 }))}
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
                />
                <p className="text-xs text-slate-400 mt-1">Minimum extracted (non-empty) fields to count as a match</p>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Status</label>
                <div className="flex items-center gap-3 h-9">
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                    data-testid="pipeline-active-toggle"
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${form.is_active ? "bg-[#006080]" : "bg-slate-200"}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.is_active ? "translate-x-5" : "translate-x-0.5"}`} />
                  </button>
                  <span className="text-sm text-slate-600">{form.is_active ? "Active" : "Inactive"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Schema selection */}
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Schemas</h3>
              <p className="text-xs text-slate-500 mt-1">Select approved schemas to evaluate. Each will be matched and extracted independently.</p>
            </div>
            {schemas.length === 0 ? (
              <p className="text-sm text-slate-400">No approved schemas found. <Link to="/schemas" className="text-[#006080] hover:underline">Create one first.</Link></p>
            ) : (
              <div className="space-y-2">
                {schemas.map(s => {
                  const selected = form.schema_ids.includes(s.id);
                  const isDraft = s.status !== "approved";
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleSchema(s.id)}
                      data-testid={`schema-toggle-${s.id}`}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${selected ? "border-[#006080] bg-[#E5F0F2]/50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${selected ? "bg-[#006080] border-[#006080]" : "border-slate-300"}`}>
                        {selected && <Check size={10} className="text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium ${selected ? "text-[#006080]" : "text-slate-800"}`}>{s.name}</p>
                          {isDraft && (
                            <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 flex-shrink-0">draft</span>
                          )}
                        </div>
                        {s.description && <p className="text-xs text-slate-400 truncate">{s.description}</p>}
                        <p className="text-xs text-slate-400">{s.fields?.filter(f => f.enabled !== false).length || 0} active fields</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {form.schema_ids.length === 0 && (
              <p className="text-xs text-amber-600">Select at least one schema to build an effective pipeline.</p>
            )}
          </div>

          {/* OCR Settings */}
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">OCR Settings</h3>
              <p className="text-xs text-slate-500 mt-1">Configure how Mistral OCR processes documents for this pipeline.</p>
            </div>

            {/* Page ranges */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">
                Page Ranges <span className="normal-case font-normal text-slate-400">(optional)</span>
              </label>
              <input
                data-testid="ocr-pages-input"
                type="text"
                value={form.ocr_pages}
                onChange={e => setForm(f => ({ ...f, ocr_pages: e.target.value }))}
                placeholder='e.g. "1-4,8" — leave blank to process all pages'
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] font-mono"
              />
              <p className="text-xs text-slate-400 mt-1">Pages are 1-indexed. Ranges and individual pages can be combined (e.g. 1-3,5,7-9).</p>
            </div>

            {/* Table format */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Extract Tables As</label>
              <div className="flex gap-2" data-testid="table-format-group">
                {[
                  { value: "", label: "Inline Markdown", desc: "Tables stay inside the page markdown" },
                  { value: "markdown", label: "Standalone Markdown", desc: "Tables extracted as separate markdown blocks" },
                  { value: "html", label: "Standalone HTML", desc: "Tables extracted as separate HTML blocks" },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`table-format-${opt.value || "inline"}`}
                    onClick={() => setForm(f => ({ ...f, ocr_table_format: opt.value }))}
                    title={opt.desc}
                    className={`flex-1 py-2 px-3 rounded-lg border text-xs font-medium transition-colors text-center ${
                      form.ocr_table_format === opt.value
                        ? "border-[#006080] bg-[#E5F0F2]/60 text-[#006080]"
                        : "border-slate-200 text-slate-600 hover:border-slate-300 bg-white"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Extract content toggles */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-2">Extract Content</label>
              <div className="flex gap-2" data-testid="extract-content-group">
                {[
                  { key: "ocr_extract_images", label: "Images", icon: "img" },
                  { key: "ocr_extract_header", label: "Header", icon: "hdr" },
                  { key: "ocr_extract_footer", label: "Footer", icon: "ftr" },
                ].map(item => (
                  <button
                    key={item.key}
                    type="button"
                    data-testid={`extract-${item.key}`}
                    onClick={() => setForm(f => ({ ...f, [item.key]: !f[item.key] }))}
                    className={`flex items-center gap-1.5 py-2 px-4 rounded-lg border text-xs font-medium transition-colors ${
                      form[item.key]
                        ? "border-[#006080] bg-[#E5F0F2]/60 text-[#006080]"
                        : "border-slate-200 text-slate-500 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-sm flex-shrink-0 ${form[item.key] ? "bg-[#006080]" : "bg-slate-300"}`} />
                    {item.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                Images: returns base64 image data per page. Header/Footer: separates them from the main content.
              </p>
            </div>

            {/* LLM Override */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">LLM Override</label>
              <div className="flex gap-2" data-testid="llm-override-group">
                {[
                  { value: "", label: "Per Schema", desc: "Respect each schema's own Call LLM setting" },
                  { value: "force_on", label: "Force On", desc: "Always call LLM for every schema in this pipeline" },
                  { value: "force_off", label: "Force Off (Regex Only)", desc: "Never call LLM — regex extraction only for all schemas" },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`llm-override-${opt.value || "per-schema"}`}
                    onClick={() => setForm(f => ({ ...f, llm_override: opt.value }))}
                    title={opt.desc}
                    className={`flex-1 py-2 px-3 rounded-lg border text-xs font-medium transition-colors text-center ${
                      form.llm_override === opt.value
                        ? "border-[#006080] bg-[#E5F0F2]/60 text-[#006080]"
                        : "border-slate-200 text-slate-600 hover:border-slate-300 bg-white"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1">
                <span className="font-medium text-slate-500">Force Off</span> skips all LLM calls — uses regex only, much faster and uses no AI credits.
              </p>
            </div>

            {/* Return raw OCR text */}
            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-800">Return raw OCR text in API response</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  When enabled, the ingest response includes an <code className="bg-slate-100 px-1 rounded font-mono">ocr_raw_text</code> field with the full extracted text.
                </p>
              </div>
              <button
                type="button"
                data-testid="ocr-return-raw-text-toggle"
                onClick={() => setForm(f => ({ ...f, ocr_return_raw_text: !f.ocr_return_raw_text }))}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ml-4 ${form.ocr_return_raw_text ? "bg-[#006080]" : "bg-slate-200"}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.ocr_return_raw_text ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
          </div>

          {/* Webhook */}
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Webhook (optional)</h3>
              <p className="text-xs text-slate-500 mt-1">Fire this webhook when at least one schema matches.</p>
            </div>
            <select
              data-testid="pipeline-webhook-select"
              value={form.webhook_id}
              onChange={e => setForm(f => ({ ...f, webhook_id: e.target.value }))}
              className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
            >
              <option value="">No webhook</option>
              {webhooks.map(w => <option key={w.id} value={w.id}>{w.name || w.url}</option>)}
            </select>
          </div>

          <div className="flex gap-3">
            <Link to="/pipelines" className="flex-1 h-10 border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50 flex items-center justify-center">
              Cancel
            </Link>
            <button
              type="submit"
              data-testid="save-pipeline-button"
              disabled={saving}
              className="flex-1 h-10 bg-[#006080] text-white text-sm font-semibold rounded-md hover:bg-[#004B66] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Save size={14} /> {saving ? "Saving..." : isNew ? "Create Pipeline" : "Save Changes"}
            </button>
          </div>
        </form>
      )}

      {/* ── Keys tab ────────────────────────────────────────────────────── */}
      {!isNew && activeTab === "keys" && (
        <div className="space-y-5">
          {/* Create new key */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Generate New Key</h3>
            <div className="flex gap-3">
              <input
                data-testid="new-key-name-input"
                value={newKeyName}
                onChange={e => setNewKeyName(e.target.value)}
                placeholder="Key name"
                className="flex-1 h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
              />
              <button
                onClick={createKey}
                disabled={creatingKey}
                data-testid="generate-key-button"
                className="h-9 px-4 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] disabled:opacity-50 flex items-center gap-2"
              >
                <Plus size={13} /> Generate
              </button>
            </div>

            {createdKey && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                <div className="flex items-center gap-2 text-amber-800">
                  <AlertCircle size={14} />
                  <span className="text-xs font-bold">Save this key now — it will not be shown again.</span>
                </div>
                <div className="flex items-center gap-2">
                  <code className={`flex-1 text-xs font-mono bg-white border border-amber-200 rounded px-3 py-2 break-all ${showCreatedKey ? "" : "filter blur-sm select-none"}`}>
                    {createdKey}
                  </code>
                  <button onClick={() => setShowCreatedKey(s => !s)} className="p-2 hover:bg-amber-100 rounded text-amber-700 flex-shrink-0">
                    {showCreatedKey ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button onClick={() => { copyToClipboard(createdKey); setCopiedCreated(true); setTimeout(() => setCopiedCreated(false), 2000); }} className="p-2 hover:bg-amber-100 rounded text-amber-700 flex-shrink-0">
                    {copiedCreated ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Existing keys */}
          <div className="space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Existing Keys ({keys.filter(k => k.is_active).length} active)</h3>
            {keys.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">No keys yet. Generate one above.</p>
            ) : (
              keys.map(k => <KeyRow key={k.id} k={k} pipelineId={id} onRefresh={refreshKeys} />)
            )}
          </div>
        </div>
      )}

      {/* ── cURL / Docs tab ─────────────────────────────────────────────── */}
      {!isNew && activeTab === "curl" && (
        <div className="space-y-5">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Ingest Endpoint</h3>
            <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded font-mono text-xs">
              <span className="px-2 py-0.5 bg-[#006080] text-white rounded text-[10px] font-bold flex-shrink-0">POST</span>
              <span className="text-slate-700 break-all">{BACKEND_URL}/api/pipelines/{id}/ingest</span>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Headers</p>
              <div className="space-y-1 font-mono text-xs">
                <div className="p-2 bg-slate-50 rounded border border-slate-200"><span className="text-[#006080]">Content-Type</span>: application/json</div>
                <div className="p-2 bg-slate-50 rounded border border-slate-200"><span className="text-[#006080]">X-Pipeline-Key</span>: pk_your_key_here</div>
              </div>
            </div>
          </div>

          <div className="bg-slate-950 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 border-b border-slate-800">
              <span className="text-xs font-mono text-slate-400">bash — cURL example</span>
              <button onClick={copyCurl} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-colors">
                <Copy size={13} />
              </button>
            </div>
            <pre className="p-5 text-xs font-mono text-slate-300 overflow-x-auto whitespace-pre">{buildCurl()}</pre>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Response Format</h3>
            <div className="bg-slate-950 rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-slate-900 border-b border-slate-800 text-xs font-mono text-slate-400">json</div>
              <pre className="p-5 text-xs font-mono text-slate-300 overflow-x-auto whitespace-pre">{buildResponse()}</pre>
            </div>
          </div>

          <div className="bg-[#E5F0F2]/50 border border-[#006080]/20 rounded-lg p-5 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#006080]">Matching Logic</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex gap-2"><span className="text-[#006080] font-bold flex-shrink-0">1.</span>Document is OCR'd. Full text is available for all schemas.</li>
              <li className="flex gap-2"><span className="text-[#006080] font-bold flex-shrink-0">2.</span>For each schema: if identifier patterns are defined, text is scoped to the matched region.</li>
              <li className="flex gap-2"><span className="text-[#006080] font-bold flex-shrink-0">3.</span>Extraction runs on the scoped text. Fields are extracted via AI + per-field regex.</li>
              <li className="flex gap-2"><span className="text-[#006080] font-bold flex-shrink-0">4.</span>A schema is <strong>matched</strong> if identifier patterns are found AND ≥ {form.min_fields_matched} field(s) are extracted.</li>
              <li className="flex gap-2"><span className="text-[#006080] font-bold flex-shrink-0">5.</span>All schemas appear in <code className="bg-slate-100 px-1 rounded font-mono text-xs">schemas_evaluated</code> — matched or not.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
